import { Box } from '@chakra-ui/react'
import { Stream } from '@cloudflare/stream-react'

/**
 * Cloudflare Stream player for videos and reels that finished encoding.
 *
 * Sizing is handled here, not by stream-react's `responsive` prop. With
 * `responsive` (its default) the library wraps the iframe in a div whose
 * padding-top is the *video's own* aspect ratio, and absolutely positions the
 * iframe inside it. That ratio is only known after the player SDK emits its
 * `resize` event, so until then the wrapper has no height and the iframe —
 * absolutely positioned at height 100% — collapses to zero, hiding the poster
 * frame entirely. Once the ratio does arrive it overrides whatever box the
 * caller laid out. So we pass `responsive={false}` and stretch the wrapper and
 * iframe to fill a box we control: either the parent (`fill`) or an
 * aspect-ratio box. The Stream player letterboxes the video inside it.
 *
 * Per-video rotation / force-vertical are intentionally NOT applied here: the
 * player is an iframe, so a CSS transform would rotate its controls too. Those
 * settings still drive the desktop playback app, which reads the Firebase
 * original.
 *
 * @param {object} cf Firestore `cf` field (Cloudflare Stream state).
 * @param {boolean} fill Fill the parent box, which must size itself.
 * @param {string} aspectRatio CSS aspect-ratio used when not filling.
 * @param {string} maxHeight Optional cap, e.g. '80vh' inside a modal.
 */
export function StreamPlayer({
    cf,
    controls = true,
    autoPlay = false,
    poster,
    fill = false,
    aspectRatio = '16 / 9',
    maxHeight,
}) {
    if (!cf?.uid) return null
    return (
        <Box
            position={fill ? 'absolute' : 'relative'}
            inset={fill ? 0 : undefined}
            width="100%"
            height={fill ? '100%' : undefined}
            aspectRatio={fill ? undefined : aspectRatio}
            maxHeight={maxHeight}
            css={{
                '& > div': { width: '100%', height: '100%' },
                '& iframe': { display: 'block', width: '100%', height: '100%', border: 0 },
            }}
        >
            {/* Keyed by uid: stream-react computes the iframe URL once and never
                updates it, so a reused instance would keep the old poster. */}
            <Stream
                key={cf.uid}
                src={cf.uid}
                customerCode={cf.customerCode || undefined}
                controls={controls}
                autoplay={autoPlay}
                poster={poster ?? cf.thumbnailUrl ?? undefined}
                responsive={false}
            />
        </Box>
    )
}
